import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';
import type { Topic, TopicSet } from '@/types/topicSets.ts';

export interface TopicInput {
  title: string;
  description: string;
}

export interface CreateTopicSetRequest {
  organisation_id: string;
  name: string;
  topics?: TopicInput[];
}

export interface UpdateTopicSetRequest {
  name?: string;
}

/**
 * Every topic set an organisation may pick from: its own, with the global
 * default at the head of the list. The backend puts it there — a set with no
 * organisation is what a track falls back to — so the list is never empty and
 * the first row is not necessarily this organisation's.
 */
export async function listTopicSets(organisationId: string): Promise<TopicSet[]> {
  const { data } = await apiFetchJson<{ data: TopicSet[] }>(
    `/topics?organisation_id=${organisationId}`,
  );
  return data;
}

/** One set with its topics, which is the only shape the detail page needs. */
export async function getTopicSet(id: string): Promise<TopicSet> {
  return apiFetchJson<TopicSet>(`/topics/${id}`);
}

export async function createTopicSet(request: CreateTopicSetRequest): Promise<TopicSet> {
  return apiFetchJson<TopicSet>('/topics', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function updateTopicSet(
  id: string,
  request: UpdateTopicSetRequest,
): Promise<TopicSet> {
  return apiFetchJson<TopicSet>(`/topics/${id}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

/**
 * Refused with a 422 while any track still reads the set — deleting one would
 * leave those tracks without the themes they are built on.
 */
export async function deleteTopicSet(id: string): Promise<void> {
  await apiFetch(`/topics/${id}`, { method: 'DELETE' });
}

export async function createTopic(setId: string, topic: TopicInput): Promise<Topic> {
  return apiFetchJson<Topic>(`/topics/${setId}/topics`, {
    method: 'POST',
    body: JSON.stringify(topic),
  });
}

export async function updateTopic(
  setId: string,
  topicId: string,
  topic: Partial<TopicInput>,
): Promise<Topic> {
  return apiFetchJson<Topic>(`/topics/${setId}/topics/${topicId}`, {
    method: 'PUT',
    body: JSON.stringify(topic),
  });
}

export async function deleteTopic(setId: string, topicId: string): Promise<void> {
  await apiFetch(`/topics/${setId}/topics/${topicId}`, { method: 'DELETE' });
}
